import React from "react";
import { CheckCircle2 } from "lucide-react";

export default function WhatIs() {
  return (
    <section className="py-20 md:py-28">
      <div className="container-cv grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="section-eyebrow">What is CoVerify</span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-primary leading-[1.1] tracking-tight">
            Connected identity verification, designed for{" "}
            <span className="text-accent">compliance-driven</span> practices.
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            CoVerify combines biometric document checks, liveness detection and AML
            screening with centralised tracking, secure record storage and audit-ready
            reporting, in one connected environment.
          </p>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            Built on an industry-leading verification engine and embedded
            inside the CoSec Global compliance suite, CoVerify reduces compliance risk,
            improves visibility, and scales with high-volume verification workflows.
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
            {[
              "End-to-end verification in <5 minutes",
              "Mobile-optimised client journey",
              "Inspection-ready audit trail",
              "Scales across hundreds of entities",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm font-medium text-primary">
                <span className="lime-tick mt-0.5"><CheckCircle2 className="h-4 w-4" /></span>
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-accent/20 to-transparent rounded-3xl blur-2xl" />
            <div className="relative rounded-3xl border border-border bg-white p-6 md:p-8 shadow-lg">
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <div className="h-2 w-2 rounded-full bg-accent" />
                <div className="text-sm font-semibold text-primary">Verification timeline</div>
              </div>
              <ol className="mt-5 space-y-5">
                {[
                  { t: "Invite sent", s: "Client receives secure link", time: "00:00" },
                  { t: "Document captured", s: "Passport / driving licence", time: "00:42" },
                  { t: "Liveness verified", s: "Selfie matched, AI passed", time: "01:17" },
                  { t: "AML cleared", s: "PEP & sanctions screened", time: "02:08" },
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div className="h-7 w-7 rounded-full border-2 border-accent bg-white grid place-items-center text-[11px] font-bold text-primary">
                        {i + 1}
                      </div>
                      {i < 3 && <div className="mt-1 h-8 w-px bg-border" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-display font-bold text-primary text-sm">{step.t}</div>
                        <span className="text-[11px] font-mono text-muted-foreground">{step.time}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{step.s}</div>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1.5">
                <CheckCircle2 className="h-4 w-4 text-accent" />
                <span className="text-xs font-bold uppercase tracking-wider text-primary">Verified · Audit-ready</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
